import api from 'api/client';
import { User } from 'api/user';
import { queryResponseValidator } from 'helpers/queryUtils';
import { ChatSwitchCoachParams, ScheduleMessageParams } from 'types';
import { z } from 'zod';

import { dateSchema } from './shared/common.schema';
import { STATUS } from './types';

interface AuthorizeChatResponse {
  status: string;
  streamToken: string;
}
export interface SwitchCoachResponse {
  status: string;
  coach: User;
  channelId: string;
}

export const scheduledMessageSchema = z.object({
  id: z.string(),
  userId: z.string(),
  userFirstName: z.string(),
  userLastName: z.string(),
  userTimezone: z.string(),
  userChannelId: z.string(),
  senderId: z.string(),
  datetime: z.string(),
  text: z.string(),
  status: z.string(),
  createdAt: z.string(),
  updatedAt: z.string().optional(),
  groupId: z.string(),
  sentAt: z.string().optional(),
  attachments: z.string().optional(),
  internalNote: z.string().optional(),
});

export type ScheduledMessageTask = z.infer<typeof scheduledMessageSchema>;

export const authorizeChat = async () => {
  const { streamToken } = await api
    .post(`chat/authorize`)
    .json<AuthorizeChatResponse>();
  return streamToken;
};

export const switchCoach = async (body: ChatSwitchCoachParams) => {
  return await api
    .put(`chat/switch_coach`, { json: body })
    .json<SwitchCoachResponse>();
};

const unassignCoachOutcomeSchema = z.discriminatedUnion('status', [
  z.object({
    userId: z.string(),
    status: z.literal('unassigned'),
    coachId: z.string(),
  }),
  z.object({ userId: z.string(), status: z.literal('no_coach') }),
  z.object({
    userId: z.string(),
    status: z.literal('call_booked'),
    nextCallAt: dateSchema,
  }),
  z.object({ userId: z.string(), status: z.literal('failed') }),
]);
const unassignCoachResponseSchema = z.object({
  status: z.enum([STATUS.OK, STATUS.ERROR]),
  data: z.array(unassignCoachOutcomeSchema),
});
export type UnassignCoachOutcome = z.infer<typeof unassignCoachOutcomeSchema>;
type UnassignCoachResponse = z.infer<typeof unassignCoachResponseSchema>;

export const unassignCoach = async (
  userIds: string[]
): Promise<UnassignCoachOutcome[]> => {
  const { data } = await api
    .put('chat/unassign_coach', { json: { userIds } })
    .json()
    .then(
      queryResponseValidator<UnassignCoachResponse>(unassignCoachResponseSchema)
    );
  return data;
};

const aiCoachStatusSchema = z.object({
  status: z.enum([STATUS.OK, STATUS.ERROR]),
  enabled: z.boolean(),
});
type AiCoachStatusResponse = z.infer<typeof aiCoachStatusSchema>;

export const getAiCoachEnabled = async (userId: string): Promise<boolean> => {
  const { enabled } = await api
    .get(`chat/ai_coach/${userId}`)
    .json()
    .then(queryResponseValidator<AiCoachStatusResponse>(aiCoachStatusSchema));
  return enabled;
};

export const setAiCoachEnabled = async (params: {
  userId: string;
  enabled: boolean;
}) => {
  return api.put('chat/ai_coach', { json: params }).json();
};

export type FindCoachResponseBody = {
  status: string;
  coach: User;
  channelId: string;
};

export const findCoach = async () => {
  const { coach } = await api
    .post(`chat/find_coach`)
    .json<FindCoachResponseBody>();
  return coach;
};

export const hideUser = async (userId: string) => {
  const response = await api.put(`chat/${userId}/hide`).json();
  return response;
};

export const createChannel = async (userId: string) => {
  const response = await api
    .post(`chat/create_channel`, { json: { userId } })
    .json<{ channelId: string }>();
  return response;
};

export const scheduleMessage = async (data: ScheduleMessageParams) => {
  return api.post('scheduledMessages/create', { json: data }).json();
};

export const editScheduledMessage = async (data: ScheduleMessageParams) => {
  return api.put('scheduledMessages/edit', { json: data }).json();
};

export const getScheduledMessages = async () => {
  return api.get('scheduledMessages').json<ScheduledMessageTask[]>();
};

export const deleteScheduledMessage = async (item: ScheduledMessageTask) => {
  if (!item.id) {
    throw new Error(`'id' is missing on the news item`);
  }
  return api.delete(`scheduledMessages/${item.id}`).json();
};
